/**
 * Buyer self-service and refund-owed contracts (plan 2026-09-09-1309, U14 —
 * R32, R33, R34, R38, R40; KTD9, KD5, KD8, KD14).
 *
 *   POST /v1/ticketed-events/by-slug/:schoolSlug/orders/:referenceCode/cancel           (public)
 *   POST /v1/ticketed-events/by-slug/:schoolSlug/orders/:referenceCode/refund-request   (public)
 *   GET  /v1/ticketed-events/:eventPublicId/refund-requests                             (admin)
 *   POST /v1/ticketed-events/:eventPublicId/refund-requests/{settle,decline}            (admin)
 *
 * The public pair is authorized the way the public order read is: the reference
 * code in the path plus the buyer's email, compared case-insensitively in
 * constant time. A wrong email answers the same 404 as an unknown code.
 *
 * No money moves anywhere in this file (KD5). "Settle" records that the school
 * refunded the buyer outside the platform, in Stripe directly; it never calls
 * Stripe.
 *
 * Naming (KTD1): the schemas say `admission`; every string a person reads says
 * "registration" or "ticket".
 */

import { z } from 'zod'
import {
  BuyerCancelRefusalReasonSchema,
  BuyerContactSchema,
  BuyerRefundStatusSchema
} from '../../schemas/entities/ticketed-event-buyer-self-service'
import { TicketedEventRefundRequestStatusSchema } from '../../schemas/entities/ticketed-event-refund-request'

// ============================================================================
// Public buyer actions
// ============================================================================

/** Body of both public buyer actions. The email is the credential, so it goes in the body, not the URL. */
export const BuyerOrderActionRequestSchema = z
  .object({
    buyerEmail: z.string().trim().min(1).max(320).email()
  })
  .strict()
export type BuyerOrderActionRequest = z.infer<typeof BuyerOrderActionRequestSchema>

/**
 * A buyer cancelled their whole order (R32, KD14). `cancelledCount` is how many
 * tickets this call removed; a repeat call answers 0 with the order still
 * `cancelled`.
 */
export const BuyerCancelOrderResponseSchema = z.object({
  data: z.object({
    status: z.literal('cancelled'),
    cancelledCount: z.number().int()
  })
})
export type BuyerCancelOrderResponse = z.infer<typeof BuyerCancelOrderResponseSchema>

/** `details` of the 409 `BUYER_CANCEL_REFUSED`. */
export const BuyerCancelRefusedDetailsSchema = z.object({
  reason: BuyerCancelRefusalReasonSchema,
  deadlineAt: z.string().nullable(),
  contact: BuyerContactSchema
})
export type BuyerCancelRefusedDetails = z.infer<typeof BuyerCancelRefusedDetailsSchema>

/**
 * The buyer's refund request on a paid order (R33). One per order: a second
 * request answers the first with `created: false`.
 */
export const BuyerRefundRequestResponseSchema = z.object({
  data: z.object({
    refundRequestId: z.string(),
    refundStatus: BuyerRefundStatusSchema,
    created: z.boolean()
  })
})
export type BuyerRefundRequestResponse = z.infer<typeof BuyerRefundRequestResponseSchema>

/** `details.reason` of the 409 `REFUND_REQUEST_REFUSED`. */
export const BuyerRefundRefusalReasonSchema = z.enum(['not_confirmed', 'not_paid'])
export type BuyerRefundRefusalReason = z.infer<typeof BuyerRefundRefusalReasonSchema>

// ============================================================================
// Admin refunds queue
// ============================================================================

/** Same bound as the registration verbs. */
export const MAX_REFUND_REQUESTS_PER_CALL = 500

/** One registration a refund row covers, as the admin queue names it. */
export const RefundRequestRegistrationSchema = z.object({
  id: z.string(),
  holderName: z.string().nullable(),
  ticketTypeName: z.string(),
  status: z.string()
})
export type RefundRequestRegistration = z.infer<typeof RefundRequestRegistrationSchema>

/**
 * One refund row on an event (KTD9). `grain` says which of the table's two
 * shapes it is: `order` is the buyer's order-wide request, `registration` is
 * the per-ticket row an admin's cancel or void of a paid ticket raised.
 */
export const AdminRefundRequestSchema = z.object({
  id: z.string(),
  grain: z.enum(['order', 'registration']),
  status: TicketedEventRefundRequestStatusSchema,
  amountCents: z.number().int(),
  requestedAt: z.string(),
  settledAt: z.string().nullable(),
  note: z.string().nullable(),
  /** Who paid. Null only for a ticket sold at the door with no holder name. */
  buyer: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().nullable()
    })
    .nullable(),
  order: z.object({ id: z.string(), referenceCode: z.string() }).nullable(),
  registrations: z.array(RefundRequestRegistrationSchema)
})
export type AdminRefundRequest = z.infer<typeof AdminRefundRequestSchema>

/** Open rows first, newest first within each group. `openCount` feeds the section's badge. */
export const ListRefundRequestsResponseSchema = z.object({
  data: z.array(AdminRefundRequestSchema),
  meta: z.object({ openCount: z.number().int() })
})
export type ListRefundRequestsResponse = z.infer<typeof ListRefundRequestsResponseSchema>

/** Settle and decline. Strict, so a misspelled field is a 400 rather than silently ignored. */
export const ResolveRefundRequestsRequestSchema = z
  .object({
    refundRequestPublicIds: z.array(z.string().min(1)).min(1).max(MAX_REFUND_REQUESTS_PER_CALL),
    note: z.string().trim().min(1).max(1000).optional()
  })
  .strict()
export type ResolveRefundRequestsRequest = z.infer<typeof ResolveRefundRequestsRequestSchema>

export const RefundRequestOutcomeKindSchema = z.enum([
  'settled',
  'declined',
  /** Already in the target state. Nothing changed (R40). */
  'already_in_state',
  /** Already closed the other way, e.g. settling a declined request. */
  'invalid_transition',
  /** Not a refund row on this event at this school. */
  'not_found'
])
export type RefundRequestOutcomeKind = z.infer<typeof RefundRequestOutcomeKindSchema>

export const RefundRequestOutcomeSchema = z.object({
  refundRequestId: z.string(),
  outcome: RefundRequestOutcomeKindSchema,
  /** The row's status after the call; null when not found. */
  status: TicketedEventRefundRequestStatusSchema.nullable()
})
export type RefundRequestOutcome = z.infer<typeof RefundRequestOutcomeSchema>

/** One outcome per distinct requested id, in request order. */
export const ResolveRefundRequestsResponseSchema = z.object({
  outcomes: z.array(RefundRequestOutcomeSchema)
})
export type ResolveRefundRequestsResponse = z.infer<typeof ResolveRefundRequestsResponseSchema>
